import { useRef } from 'react';
import { Field, useFormikContext } from 'formik';
import { Box, ButtonBase, Typography } from '@mui/material';

import { DocumentSeriesResetPeriod, DocumentSeriesResetPeriodList } from '../../config/documentSeries';
import { CheckboxUI, FormErrorUI, FormLabelUI, InputUI, SelectUI } from '../Interface';

const formatTokens = [
  { token: '{YY}', description: 'Kétjegyű év' },
  { token: '{YYYY}', description: 'Négyjegyű év' },
  { token: '{MM}', description: 'Hónap' },
  { token: '{DD}', description: 'Nap' },
  { token: '{DT}', description: 'Bizonylattípus' },
  { token: '{NNNNN}', description: 'Futó sorszám; az N-ek száma adja a hosszát' },
];

const yearFormatTokens = new Set(['{YY}', '{YYYY}']);

const FormatTokenSelector = ({ disabled, disableYearTokens, onInsertToken }) => (
  <Box
    className="col-span-4"
    sx={{
      mt: 2,
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      backgroundColor: 'grey.50',
    }}
  >
    <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
      Formátum helyőrzők
    </Typography>
    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
      Kattints a beszúrni kívánt értékre.
    </Typography>
    <Box
      sx={{
        mt: 1.5,
        display: 'grid',
        gap: 1,
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        },
      }}
    >
      {formatTokens.map((item) => (
        <ButtonBase
          key={item.token}
          disabled={disabled || (disableYearTokens && yearFormatTokens.has(item.token))}
          onMouseDown={(event) => {
            event.preventDefault();
            onInsertToken(item.token);
          }}
          sx={{
            display: 'block',
            width: '100%',
            px: 1.25,
            py: 1,
            textAlign: 'left',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'common.white',
            boxShadow: 1,
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'grey.50',
            },
            '&.Mui-disabled': {
              opacity: 0.6,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {item.token}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>
              {item.description}
            </Typography>
          </Box>
        </ButtonBase>
      ))}
    </Box>
  </Box>
);

const DocumentSeriesFields = ({ viewField }) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();
  const formatInputRef = useRef(null);
  const formatSelectionRef = useRef({ start: 0, end: 0 });

  const syncFormatSelection = () => {
    const input = formatInputRef.current;
    if (!input) return;

    formatSelectionRef.current = {
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? input.selectionStart ?? 0,
    };
  };

  const insertFormatToken = (token) => {
    const input = formatInputRef.current;
    const currentValue = values.format ?? '';
    const start = input ? formatSelectionRef.current.start : currentValue.length;
    const end = input ? formatSelectionRef.current.end : currentValue.length;
    const nextValue = currentValue.slice(0, start) + token + currentValue.slice(end);
    const nextPosition = start + token.length;

    setFieldValue('format', nextValue).then(() => validateField('format'));
    formatSelectionRef.current = { start: nextPosition, end: nextPosition };

    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(nextPosition, nextPosition);
    });
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5 items-center">
      <div className="col-span-4 md:col-span-2">
        <Field
          type="text"
          name="name"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Megnevezés" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.name} />}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.name) }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          name="resetPeriod"
          component={SelectUI}
          fullWidth
          option={DocumentSeriesResetPeriodList}
          value={values.resetPeriod ?? ''}
          variant="standard"
          label={<FormLabelUI text="Újraindítás" />}
          helperText={<FormErrorUI message={errors.resetPeriod} />}
          disabled={viewField || values.generated}
          onChange={(event) => {
            const value = event.target.value;
            setFieldValue('resetPeriod', value).then(() => validateField('resetPeriod'));
            setFieldValue(
              'numberingYear',
              value === DocumentSeriesResetPeriod.YEAR ? new Date().getFullYear() : null
            );
          }}
          InputLabelProps={{ shrink: Boolean(values.resetPeriod) }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="number"
          name="numberingYear"
          as={InputUI}
          value={
            values.resetPeriod === DocumentSeriesResetPeriod.YEAR
              ? new Date().getFullYear()
              : values.numberingYear ?? ''
          }
          fullWidth
          label={<FormLabelUI text="Számozási év" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.numberingYear} />}
          disabled={viewField || values.generated || values.resetPeriod === DocumentSeriesResetPeriod.YEAR}
          inputProps={{ min: 2021, step: 1 }}
          InputLabelProps={{
            shrink:
              values.resetPeriod === DocumentSeriesResetPeriod.YEAR ||
              (values.numberingYear !== null && values.numberingYear !== ''),
          }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="number"
          name="nextNumber"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Következő sorszám" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.nextNumber} />}
          disabled={viewField || values.generated}
          inputProps={{ min: 1, step: 1 }}
          InputLabelProps={{ shrink: values.nextNumber !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          name="active"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Aktív" />}
          checked={Boolean(values.active)}
          disabled={viewField}
          onChange={(event) => {
            const active = event.target.checked;
            setFieldValue('active', active);
            if (!active) {
              setFieldValue(
                'documentSeriesDocType',
                (values.documentSeriesDocType ?? []).map((item) => ({ ...item, isDefault: false }))
              );
            }
          }}
        />
      </div>
      <div className="col-span-4">
        <Field
          type="text"
          name="format"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Formátum" />}
          placeholder="BSZ-{YY}-{NNNN}"
          variant="standard"
          helperText={<FormErrorUI message={errors.format} />}
          disabled={viewField || values.generated}
          InputLabelProps={{ shrink: Boolean(values.format) }}
          inputRef={formatInputRef}
          onClick={syncFormatSelection}
          onKeyUp={syncFormatSelection}
          onSelect={syncFormatSelection}
        />
      </div>
      <FormatTokenSelector
        disabled={viewField || values.generated}
        disableYearTokens={
          values.resetPeriod === DocumentSeriesResetPeriod.NEVER &&
          (values.numberingYear === null || values.numberingYear === '')
        }
        onInsertToken={insertFormatToken}
      />
    </div>
  );
};

export default DocumentSeriesFields;
